import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { FieldWorkRequest } from '../../domain/fieldWorkRequest';
import { isOpenOrder, orderProgress, orderTitle } from '../../domain/fulfillment';
import { NewFulfillmentOrderDrawer } from '../fulfillment/NewFulfillmentOrderDrawer';
import { OrderStateBadge, ProgressMeter } from '../fulfillment/OrderStateBadge';
import { ShipmentLink } from '../shipments/ShipmentLink';
import { useRequestPickLists } from './request-shipments';

/** The pick lists packed for a request, and a way to start another. */
export function PickListsSection({
  request,
  disabled,
}: {
  request: FieldWorkRequest;
  disabled: boolean;
}) {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const orders = useRequestPickLists(request.id);
  const rows = orders.data ?? [];
  const open = rows.filter(isOpenOrder).length;

  return (
    <section className="drawer-section" aria-label="Pick lists">
      <h2>Pick lists</h2>
      {orders.isLoading ? <p>Loading pick lists…</p> : null}
      {orders.isError ? (
        <p role="alert" className="form-error">
          Pick lists could not be loaded.{' '}
          <button type="button" className="table-filter-link" onClick={() => void orders.refetch()}>
            Retry
          </button>
        </p>
      ) : null}
      {orders.isSuccess && rows.length === 0 ? <p>No pick list yet.</p> : null}
      {rows.length > 0 ? (
        <>
          <ul className="compact-list">
            {rows.map((order) => {
              const progress = orderProgress(order);
              return (
                <li key={order.id}>
                  <Link to={`/fulfillment/${order.id}`}>{orderTitle(order)}</Link>{' '}
                  <OrderStateBadge state={order.state} />{' '}
                  <ProgressMeter
                    complete={progress.complete}
                    total={progress.total}
                    label={`${orderTitle(order)}: ${progress.complete} of ${progress.total} picked`}
                  />
                  {order.shipmentId ? (
                    <>
                      {' · '}
                      <ShipmentLink shipmentId={order.shipmentId} />
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <p className="field-hint">
            {open === 0
              ? 'Every pick list for this request is packed or cancelled.'
              : `${open} of ${rows.length} still open.`}
          </p>
        </>
      ) : null}
      <div className="drawer-actions">
        <button type="button" disabled={disabled} onClick={() => setCreating(true)}>
          Create pick list
        </button>
      </div>
      {creating ? (
        <NewFulfillmentOrderDrawer
          defaults={{
            customerId: request.customerId,
            siteId: request.siteId,
            fieldWorkRequestId: request.id,
            fieldWorkRequestTitle: request.title || 'Untitled request',
          }}
          onClose={() => setCreating(false)}
          onCreated={(order) => void navigate(`/fulfillment/${order.id}`)}
        />
      ) : null}
    </section>
  );
}
